// @flow

import React, { Fragment } from "react";
import { Input, View } from "@components";
import { flattenNodes, getParentIds } from "../base-hierarchy-tree/util";
import type { ComponentType } from "react";
import type { FlatNodes } from "../base-hierarchy-tree/util";
import type { TreeBranch } from "../base-hierarchy-tree";

type OwnProps = $Exact<{
  tree: TreeBranch,
  searchable: boolean,
  searchPlaceholder: string,
}>;

type OwnState = $Exact<{
  search: string | null,
}>;

const withSearchable = <
  Props,
  Component: ComponentType<Props>,
  OutProps: $Exact<{ ...Props, ...OwnProps }>,
>(
  WrappedComponent: Component,
): ComponentType<OutProps> => {
  class withSearchable extends React.Component<OutProps, OwnState> {
    flatNodes: FlatNodes;

    constructor(props: OutProps) {
      super(props);

      this.state = {
        search: null,
      };

      this.flatNodes = flattenNodes(props.tree);
    }

    setSearchString = (event: Object) => {
      this.setState({ search: event.target.value });
    };

    getTree = (matchingNodes: number[]): TreeBranch | null => {
      if (!this.state.search) {
        return this.props.tree;
      }

      return this.filterBranch(this.props.tree, matchingNodes);
    };

    filterBranch = (branch: TreeBranch, matches: number[]): TreeBranch | null => {
      let filteredBranch: TreeBranch;

      if (matches.indexOf(branch.data.id) === -1) {
        return null;
      }

      let branchChildren: TreeBranch[] = [];
      for (let node of branch.children) {
        let id = node.data.id;
        if (matches.indexOf(id) !== -1) {
          let filtered = this.filterBranch(node, matches);
          if (filtered !== null) {
            branchChildren.push(filtered);
          }
        }
      }

      filteredBranch = {
        ...branch,
        children: branchChildren,
      };

      return filteredBranch;
    };

    get matchingNodes(): number[] {
      if (!this.state.search) {
        return [];
      }

      // Escape RegEx special characters as per MDN recommendation:
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
      const searchRegex = new RegExp(
        this.state.search.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&"),
        "i",
      );

      // Root node should always be there
      let matchingNodes: number[] = [this.props.tree.data.id];
      for (let id in this.flatNodes) {
        id = parseInt(id);
        if (searchRegex.test(this.flatNodes[id].label)) {
          matchingNodes = matchingNodes.concat(getParentIds(this.flatNodes, id));
          matchingNodes.push(parseInt(id));
        }
      }

      return Array.from(new Set(matchingNodes));
    }

    render() {
      const matchingNodes: number[] = this.matchingNodes;
      const tree: TreeBranch | null = this.getTree(matchingNodes);

      return (
        <View>
          {this.props.searchable && (
            <Input
              className="w-100 mb-3"
              id="search"
              name="search"
              placeholder={this.props.searchPlaceholder}
              onChange={this.setSearchString}
            />
          )}
          <WrappedComponent {...this.props} tree={tree} expanded={matchingNodes} />
        </View>
      );
    }
  }

  return withSearchable;
};

withSearchable.defaultProps = {
  searchable: false,
  searchPlaceholder: "Search...",
  expanded: [],
};

export default withSearchable;
